/* eslint-disable @typescript-eslint/naming-convention */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { screensMap } from '..';
import IncomeVerificationPopup from './IncomeVerificationInfo';
import Input from '@/lib/Input';
import Chip from '@/lib/Chip';
import Icon from '@/lib/Icon/Icon';
import AutoComplete from '@/lib/Autocomplete';
import { formRequest } from '@/utils/helpers';
import Button from '@/lib/Button';
import { CustomPopupWindow } from '@/utils/popup';
import { searchCompany } from '@/api/employer-verifivcation';
import { AutocompleteItem } from '@/lib/Autocomplete/types';
import useDebounce from '@/hooks/useDebounce';
import { PaymentCancelWrapper } from '@/shared/Components/PaymentCancelWrapper';
import OpenPopupLoader from '@/shared/Components/OpenPopupLoader';
import EmiPlansLoader from '@/shared/Components/EmiPlansLoader';
import { updatePayment } from '@/api/payment';
import CheckBox from '@/lib/CheckBox';
import { PopupFailureProps } from '@/shared/types';
import { getUserLoggedInStatus, trackHelper } from '@/analytics/utils';
import { ANALYTICS_ACTIONS, EVENT_NAMES } from '@/analytics/constants';
import { useGlobalStore } from '@/store';
import { ErrorPayload } from '@/api/types';

const EmployerDetails = (): JSX.Element => {
  const [selectedEmployer, setSelectedEmployer] = useState({
    id: '',
    name: '',
  });
  const [employer, setEmployer] = useState('');
  const [email, setEmail] = useState('');
  const [isEmailValid, setEmailValid] = useState(true);
  const [showLoader, setShowLoader] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [showError, setShowError] = useState(false);
  const [isSavingDetails, setIsSavingDetails] = useState(false);
  const employerVerificationRef = useRef<string | null>(null);
  const [employerOptions, setEmployerOptions] = useState<AutocompleteItem[]>([]);
  const [isEmployerAbsent, setisEmployerAbsent] = useState(false);
  const [employerAbsentSelected, setEmployerAbsentSelected] = useState(false);
  const [incomeVerificationError, setIncomeVerificationError] = useState('');
  const { debounce } = useDebounce();
  const { user, userAccessToken } = useGlobalStore();

  const popupRef = useRef<CustomPopupWindow | null>(null);

  const handleSelect = (value: AutocompleteItem): void => {
    setSelectedEmployer(value);
    // Track employer name added
    trackHelper({
      actionName: ANALYTICS_ACTIONS.INPUT,
      objectName: EVENT_NAMES.EMPLOYER_NAME_ADDED,
      screen: screensMap.EMPLOYER_DETAILS,
      properties: {
        user_type: getUserLoggedInStatus(userAccessToken, user.contact),
        contact: user.contact,
        employer_name: value.name,
      },
    });
  };

  const handleClick = async (): Promise<void> => {
    if (isEmployerAbsent && employerAbsentSelected) {
      try {
        const res = await updatePayment({
          status: 'failed',
          // eslint-disable-next-line @typescript-eslint/naming-convention
          failure_reason: 'cancelled_by_user',
        });
        if (res.data.gateway_payload) {
          const { url, payload, method } = res.data.gateway_payload;
          formRequest(url, payload, method);
        }
      } catch (error: unknown) {
        //
      }
    }

    if (!selectedEmployer.id) {
      return;
    }

    // Track Verify CTA clicked
    trackHelper({
      actionName: ANALYTICS_ACTIONS.CLICK,
      objectName: EVENT_NAMES.EMPLOYER_VERIFY_CLICKED,
      screen: screensMap.EMPLOYER_DETAILS,
      properties: {
        user_type: getUserLoggedInStatus(userAccessToken, user.contact),
        contact: user.contact,
        employer_name: selectedEmployer.name,
      },
    });

    setShowInfo(true);
  };

  const handleSuccess = (url: string): void => {
    setShowInfo(false);
    setShowLoader(true);
    employerVerificationRef.current = url;
  };

  const handleError = (err: unknown): void => {
    setShowInfo(false);
    setShowError(true);
    // Track Income verofy popup render
    trackHelper({
      actionName: ANALYTICS_ACTIONS.RENDER,
      objectName: EVENT_NAMES.INCOME_VERIFY_FAILED,
      screen: screensMap.EMPLOYER_DETAILS,
      properties: {
        user_type: getUserLoggedInStatus(userAccessToken, user.contact),
        contact: user.contact,
        failure_reason: err ? (err as ErrorPayload).details?.[0].description : '',
      },
    });
  };

  useEffect(() => {
    // Track Employer verfication screen render
    trackHelper({
      actionName: ANALYTICS_ACTIONS.RENDER,
      objectName: EVENT_NAMES.EMPLOYER_VERIFY_RENDER,
      screen: screensMap.EMPLOYER_DETAILS,
      properties: {
        contact: user.contact,
        user_type: getUserLoggedInStatus(userAccessToken, user.contact),
      },
    });
    // Cleanup when the component unmounts
    return () => {
      if (popupRef.current) {
        setShowLoader(false);
        // Clean up any resources related to the popup
        // For example, you might want to close the popup
        popupRef.current.close();
      }
    };
  }, []);

  const fetchEmployerResults = async (val: string): Promise<void> => {
    const res = await searchCompany(val);
    setisEmployerAbsent(false);
    try {
      if (res.entities.length) {
        setEmployerOptions(res.entities);
      } else {
        setisEmployerAbsent(true);
      }
    } catch {
      setisEmployerAbsent(true);
    }
  };

  const handleEmployerSearch = useCallback(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    debounce((val: string) => fetchEmployerResults(val), 500),
    [],
  );

  const handleCheckBoxClick = (): void => {
    setEmployerAbsentSelected(!employerAbsentSelected);
  };

  const handleIncomeVerificationError = (e: PopupFailureProps): void => {
    if (e.message) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      setIncomeVerificationError((e.message as unknown as any)?.description || '');
      setShowError(true);
      setShowLoader(false);
    }
  };

  return (
    <>
      <div className="relative h-full d:flex d:items-center d:justify-center">
        <div className="d:w-70 d:relative h-full d:h-fit">
          <div className="py-3 px-6 h-full grow overflow-scroll d:grow-0 d:h-fit d:p-0">
            <h3 className="font-tasa font-semibold text-2xl text-on-grey-normal mb-5">
              Enter employer details for secure access to your income statement
            </h3>
            <AutoComplete
              options={employerOptions}
              placeholder="Employer name"
              onSelect={handleSelect}
              selected={selectedEmployer}
              showDefault={false}
              onChange={(val: string): void => handleEmployerSearch(val)}
            />
            {selectedEmployer.name.toLowerCase() === 'others' ? (
              <Input
                placeholder="Employer name"
                value={employer}
                onChange={(e): void => {
                  setEmployer(e.target.value);
                }}
              />
            ) : null}
            <Chip className="[&&]:bg-information-default [&&]:bg-opacity-18 [&&]:p-2.5 [&&]:px-2 border-0 mt-1.5 mb-6 [&&]:rounded-lg">
              <Icon name="error" className="mr-1.5" fill="fill-information-intense" />
              <p className="text-sm text-information-intense">As per your salary slip/payslip</p>
            </Chip>
            {isEmployerAbsent ? (
              <CheckBox onClick={handleCheckBoxClick} checked={employerAbsentSelected}>
                Company isn’t listed/Self employed/Unemployed
              </CheckBox>
            ) : null}
            {isEmployerAbsent && employerAbsentSelected ? (
              <Chip className="[&&]:bg-notice-faded [&&]:bg-opacity-9 [&&]:p-2.5 [&&]:px-2 border-0 mt-1.5 mb-6 [&&]:rounded-lg">
                <Icon name="warning" className="w-8" fill="fill-notice-intense" />
                <p className="text-xs d:text-sm ml-1.5 text-notice-intense">
                  Our partner lenders require your employer details for the KYC. Kindly check again
                  later or change payment method to complete the transaction
                </p>
              </Chip>
            ) : (
              ''
            )}
          </div>
          <div className="shrink-0 align-bottom p-6 pt-2 w-full shadow-top absolute bottom-0 d:relative d:shrink-unset d:shadow-none">
            <Button
              onClick={(): void => {
                handleClick();
              }}
              className="w-full"
            >
              {isEmployerAbsent && employerAbsentSelected
                ? 'Change payment method'
                : ' Verify employer'}
            </Button>
          </div>
        </div>
      </div>

      {showLoader ? (
        <OpenPopupLoader
          serviceType="incomeVerification"
          onFail={handleIncomeVerificationError}
          serviceName="Perfios"
          url={employerVerificationRef.current as string}
          progressText="Income verification is in progress on"
          secondaryText="This might take a few minutes"
          onSuccess={(): void => {
            setIsSavingDetails(true);
            setShowLoader(false);
          }}
        />
      ) : null}
      {showInfo ? (
        <IncomeVerificationPopup
          isOpen={showInfo}
          onDismiss={(): void => {
            setShowInfo(false);
          }}
          onSuccess={handleSuccess}
          onError={handleError}
          data={{
            id: selectedEmployer.id,
            name: selectedEmployer.name,
            email,
          }}
        />
      ) : null}

      {showError ? (
        <PaymentCancelWrapper
          title="Income verification failed"
          description={incomeVerificationError}
          isOpen={showError}
          ctaText="Retry"
          onDismiss={(): void => {
            setShowError(false);
            setShowLoader(false);
          }}
          onCtaClick={(): void => {
            setShowError(false);
            setShowLoader(false);
          }}
        />
      ) : null}

      {isSavingDetails ? (
        <EmiPlansLoader
          isLoading={isSavingDetails}
          onDismiss={(): void => {
            setIsSavingDetails(false);
          }}
        />
      ) : null}
    </>
  );
};

export default EmployerDetails;
